import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, X, ChevronLeft, ChevronRight } from "lucide-react";
import Header from "../components/Header";
import { playTick } from "../lib/tick";
import { useEdgeSwipeBack } from "../lib/useEdgeSwipeBack";

// A single pill-shaped image tile. Click opens lightbox.
const PillImage = ({ src, alt, className = "", bg, onClick, scale = 1, position = "center" }) => (
  <button
    onClick={onClick}
    className={`group relative overflow-hidden rounded-[14px] md:rounded-[20px] block w-full ${className}`}
    style={{ backgroundColor: bg || "#EFEDE6" }}
    aria-label={`Open ${alt}`}
  >
    <img
      src={src}
      alt={alt}
      loading="lazy"
      style={{
        objectPosition: position,
        transform: scale !== 1 ? `scale(${scale})` : undefined,
        transformOrigin: "center"
      }}
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
    />
  </button>
);

const Lightbox = ({ tiles, index, setIndex, onClose }) => {
  const prev = useCallback(() => setIndex((i) => (i - 1 + tiles.length) % tiles.length), [tiles.length, setIndex]);
  const next = useCallback(() => setIndex((i) => (i + 1) % tiles.length), [tiles.length, setIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, prev, next]);

  const t = tiles[index];
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-paper text-ink flex items-center justify-center hover:bg-ink hover:text-paper border border-paper transition-colors duration-300 shadow-lg"
      >
        <X className="w-5 h-5" strokeWidth={1.75} />
      </button>
      <div className="absolute top-4 left-4 md:top-6 md:left-6 mono text-xs uppercase tracking-widest text-paper/60">
        {String(index + 1).padStart(2, "0")} / {String(tiles.length).padStart(2, "0")}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); prev(); }}
        aria-label="Previous"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-paper text-ink flex items-center justify-center hover:bg-ink hover:text-paper border border-paper transition-colors duration-300 z-10 shadow-lg"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.75} />
      </button>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-5xl max-h-[80vh] flex items-center justify-center cursor-default"
      >
        <div
          className="flex items-center justify-center max-w-full max-h-[80vh]"
          style={{ backgroundColor: t.bg || "transparent" }}
        >
          <img src={t.large || t.src} alt={t.alt} className="max-w-full max-h-[80vh] w-auto h-auto object-contain shadow-2xl" />
        </div>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); next(); }}
        aria-label="Next"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 md:w-14 md:h-14 rounded-full bg-paper text-ink flex items-center justify-center hover:bg-ink hover:text-paper border border-paper transition-colors duration-300 z-10 shadow-lg"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" strokeWidth={1.75} />
      </button>
    </div>
  );
};

const ProjectDetailEditorial = ({ project, next, isLooping = false }) => {
  useEdgeSwipeBack();
  const nav = useNavigate();
  const [lbIdx, setLbIdx] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [project.slug]);

  // Normalise gallery items so we always have { src, bg, alt, scale, position, large } objects
  const items = (project.gallery || []).map((g, i) => {
    const obj = typeof g === "string" ? { url: g } : g;
    return {
      src: obj.url,
      large: obj.large || obj.url,
      bg: obj.bg || null,
      scale: obj.scale || 1,
      position: obj.position || "center",
      alt: `${project.title} — detail ${String(i + 1).padStart(2, "0")}`
    };
  });

  // Group narrative + images: 4 hero images, then alternating section + 2-image pair
  const sections = project.narrative || [];
  const hero4 = items.slice(0, 4);
  const pair1 = items.slice(4, 6);
  const pair2 = items.slice(6, 8);

  return (
    <main className="bg-paper text-ink min-h-screen">
      <Header />

      {/* Title block */}
      <section className="pt-32 md:pt-40 pb-10 md:pb-16 px-6 md:px-16 lg:px-24">
        <div className="max-w-[1500px] mx-auto">
          <button
            onClick={() => { playTick(); nav(-1); }}
            className="inline-flex items-center gap-2 mono text-xs uppercase tracking-widest text-muted-ink hover:text-ink mb-12 md:mb-16"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.25} /> Back
          </button>

          <p
            className="text-[11px] md:text-xs uppercase tracking-[0.2em] text-ink/70 mb-6"
            style={{ fontFamily: "'Avenir Next', 'Avenir', 'Nunito Sans', 'Inter', sans-serif", fontWeight: 400 }}
          >
            {project.client.replace(/—|-/g, "-").toUpperCase()}
          </p>

          <div className="max-w-[80ch]">
            <h1 className="sohne tracking-tight leading-[0.95] text-4xl sm:text-5xl md:text-5xl lg:text-6xl xl:text-[64px]">
              {project.title}
            </h1>

            <p className="mt-8 md:mt-10 text-[13px] md:text-sm leading-[1.7] text-ink/85 font-light">
              {project.summary}
            </p>
          </div>
        </div>
      </section>

      {/* HERO gallery — first row 60/40 split, second row 50/50 */}
      {hero4.length > 0 && (
        <section className="px-6 md:px-16 lg:px-24 py-6 md:py-10">
          <div className="max-w-[1500px] mx-auto space-y-3 md:space-y-3">
            {/* Row 1: 60% / 40% — both share the same height */}
            <div className="grid grid-cols-1 md:grid-cols-[6fr_4fr] gap-3 md:gap-3 items-stretch">
              {hero4[0] && <PillImage src={hero4[0].src} alt={hero4[0].alt} bg={hero4[0].bg} scale={hero4[0].scale} position={hero4[0].position} className="aspect-[16/8]" onClick={() => setLbIdx(0)} />}
              {hero4[1] && <PillImage src={hero4[1].src} alt={hero4[1].alt} bg={hero4[1].bg} scale={hero4[1].scale} position={hero4[1].position} className="aspect-[16/8] md:aspect-auto md:h-full" onClick={() => setLbIdx(1)} />}
            </div>
            {/* Row 2: 40% / 60% — height matches row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-[4fr_6fr] gap-3 md:gap-3 items-stretch">
              {hero4[2] && <PillImage src={hero4[2].src} alt={hero4[2].alt} bg={hero4[2].bg} scale={hero4[2].scale} position={hero4[2].position} className="aspect-[16/8] md:aspect-auto md:h-full" onClick={() => setLbIdx(2)} />}
              {hero4[3] && <PillImage src={hero4[3].src} alt={hero4[3].alt} bg={hero4[3].bg} scale={hero4[3].scale} position={hero4[3].position} className="aspect-[16/8]" onClick={() => setLbIdx(3)} />}
            </div>
          </div>
        </section>
      )}

      {/* Section 1: heading + body */}
      {sections[0] && (
        <section className="px-6 md:px-16 lg:px-24 py-14 md:py-20">
          <div className="max-w-[1500px] mx-auto">
            <h3 className="text-[15px] md:text-[17px] font-semibold tracking-tight mb-5">
              {sections[0].heading}
            </h3>
            <div className="space-y-4 max-w-[80ch] text-[13px] md:text-[15px] font-light leading-[1.7] text-ink/85">
              {(Array.isArray(sections[0].body) ? sections[0].body : [sections[0].body]).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pair 1 */}
      {pair1.length > 0 && (
        <section className="px-6 md:px-16 lg:px-24 py-6 md:py-10">
          <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-3">
            {pair1[0] && <PillImage src={pair1[0].src} alt={pair1[0].alt} bg={pair1[0].bg} className="aspect-square" onClick={() => setLbIdx(4)} />}
            {pair1[1] && <PillImage src={pair1[1].src} alt={pair1[1].alt} bg={pair1[1].bg} className="aspect-square" onClick={() => setLbIdx(5)} />}
          </div>
        </section>
      )}

      {/* Section 2 */}
      {sections[1] && (
        <section className="px-6 md:px-16 lg:px-24 pt-14 md:pt-20 pb-20 md:pb-28">
          <div className="max-w-[1500px] mx-auto">
            <h3 className="text-[15px] md:text-[17px] font-semibold tracking-tight mb-5">
              {sections[1].heading}
            </h3>
            <div className="space-y-4 max-w-[80ch] text-[13px] md:text-[15px] font-light leading-[1.7] text-ink/85">
              {(Array.isArray(sections[1].body) ? sections[1].body : [sections[1].body]).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pair 2 */}
      {pair2.length > 0 && (
        <section className="px-6 md:px-16 lg:px-24 pb-6 md:pb-10">
          <div className="max-w-[1500px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-3">
            {pair2[0] && <PillImage src={pair2[0].src} alt={pair2[0].alt} bg={pair2[0].bg} className="aspect-square" onClick={() => setLbIdx(6)} />}
            {pair2[1] && <PillImage src={pair2[1].src} alt={pair2[1].alt} bg={pair2[1].bg} className="aspect-square" onClick={() => setLbIdx(7)} />}
          </div>
        </section>
      )}

      {/* Section 3 */}
      {sections[2] && (
        <section className="px-6 md:px-16 lg:px-24 py-14 md:py-20">
          <div className="max-w-[1500px] mx-auto">
            <h3 className="text-[15px] md:text-[17px] font-semibold tracking-tight mb-5">
              {sections[2].heading}
            </h3>
            <div className="space-y-4 max-w-[80ch] text-[13px] md:text-[15px] font-light leading-[1.7] text-ink/85">
              {(Array.isArray(sections[2].body) ? sections[2].body : [sections[2].body]).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Next project */}
      <section className="border-t border-ink py-14 md:py-20 px-6 md:px-16 lg:px-24 mt-10">
        <div className="max-w-[1500px] mx-auto">
          <Link to={`/work/${next.slug}`} onClick={() => playTick()} className="group flex items-center justify-between">
            <div>
              <div className="mono text-[11px] uppercase tracking-widest text-muted-ink mb-3">
                {isLooping ? "Back to first project" : "Next project"}
              </div>
              <div className="sohne text-3xl md:text-5xl tracking-tight transition-all duration-500">
                {next.title}
              </div>
            </div>
            <ArrowUpRight className="w-9 h-9 md:w-12 md:h-12 transition-transform duration-500 group-hover:rotate-45" strokeWidth={1} />
          </Link>
        </div>
      </section>

      {lbIdx !== null && (
        <Lightbox tiles={items} index={lbIdx} setIndex={setLbIdx} onClose={() => setLbIdx(null)} />
      )}
    </main>
  );
};

export default ProjectDetailEditorial;
